import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaSearch, FaCheckCircle, FaTimesCircle, FaEye, FaTrash, FaUserShield, FaIdCard, FaMapMarkerAlt, FaCalendarAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null); // For modal

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/users');
            setUsers(res.data);
            setFilteredUsers(res.data);
        } catch (err) {
            console.error(err);
            alert("Failed to load users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const lower = searchTerm.toLowerCase();
        const filtered = users.filter(user => 
            user.username?.toLowerCase().includes(lower) ||
            user.email?.toLowerCase().includes(lower) ||
            user.contact_No?.includes(lower) ||
            user.profile?.name?.toLowerCase().includes(lower)
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const handleVerify = async (userId, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'unverify' : 'verify'} this user?`)) return;
        try {
            await apiClient.put(`/users/${userId}`, { is_Verified: !currentStatus });
            // Optimistic update
            setUsers(users.map(u => u.id === userId ? { ...u, is_Identity_Verified: !currentStatus } : u));
        } catch (err) {
            alert("Failed to update status.");
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Are you sure you want to DELETE this user? This action cannot be undone.")) return;
        try {
            await apiClient.delete(`/users/${userId}`);
            setUsers(users.filter(u => u.id !== userId));
        } catch (err) {
            alert("Failed to delete user.");
        }
    };

    const UserModal = () => {
        if (!selectedUser) return null;
        const profile = selectedUser.profile || {};
        const roles = selectedUser.Roles?.map(r => r.RoleName).join(', ') || 'User';
        const location = profile.location || {};
        const address = location.granular_location 
            ? `${location.granular_location}, ${location.upazila}, ${location.district}`
            : 'Address not set';

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
                <div className="bg-white w-full max-w-3xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col h-auto max-h-[90vh] mt-9">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 flex-shrink-0">
                        <h3 className="text-xl font-black text-slate-800">User Profile & Documents</h3>
                        <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-slate-200 rounded-full text-slate-500">
                            <FaTimesCircle size={20} />
                        </button>
                    </div>
                    
                    <div className="p-8 overflow-y-auto custom-scrollbar flex-grow space-y-8">
                        
                        {/* Header Info */}
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                            <img 
                                src={loadImage(profile.picture)} 
                                alt="Profile" 
                                className="w-24 h-24 rounded-full object-cover border-4 border-slate-100 shadow-md bg-slate-200"
                            />
                            <div className="flex-grow">
                                <h2 className="text-3xl font-black text-slate-800">{profile.name || selectedUser.username}</h2>
                                <p className="text-slate-500 font-bold">@{selectedUser.username}</p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <span className="px-3 py-1 bg-slate-800 text-white rounded-lg text-xs font-bold uppercase">{roles}</span>
                                    {selectedUser.is_Identity_Verified ? (
                                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold uppercase flex items-center gap-1"><FaCheckCircle /> Verified</span>
                                    ) : (
                                        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-bold uppercase flex items-center gap-1"><FaUserShield /> Pending Review</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Personal Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="font-bold text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100 pb-2">Contact Info</h4>
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><FaEnvelope /></div>
                                    <span className="font-bold text-slate-700">{selectedUser.email}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center"><FaPhone /></div>
                                    <span className="font-bold text-slate-700">{selectedUser.contact_No}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center"><FaMapMarkerAlt /></div>
                                    <span className="font-bold text-slate-700">{address}</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-bold text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100 pb-2">System Meta</h4>
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><FaCalendarAlt /></div>
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold">Joined</p>
                                        <p className="font-bold text-slate-700">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                {/* Add Last Login if available in backend later */}
                            </div>
                        </div>

                        {/* ID Documents Section */}
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                            <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-lg">
                                <FaIdCard className="text-slate-400" /> KYC Documents
                            </h4>
                            
                            {profile.id_document_url ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                        <div className="bg-white p-3 rounded-xl border border-slate-200">
                                            <p className="text-xs text-slate-400 font-bold uppercase">ID Type</p>
                                            <p className="font-bold text-slate-800">{profile.id_type || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl border border-slate-200">
                                            <p className="text-xs text-slate-400 font-bold uppercase">ID Number</p>
                                            <p className="font-bold text-slate-800">{profile.id_number || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="rounded-xl overflow-hidden border-2 border-slate-200 bg-white shadow-sm">
                                        <a href={loadImage(profile.id_document_url)} target="_blank" rel="noopener noreferrer">
                                            <img 
                                                src={loadImage(profile.id_document_url)} 
                                                alt="ID Document" 
                                                className="w-full h-auto max-h-[400px] object-contain hover:opacity-95 transition-opacity cursor-zoom-in" 
                                            />
                                        </a>
                                    </div>
                                    <p className="text-center text-xs text-slate-400 font-bold mt-2">Click image to view full size</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-10 text-slate-400 border-2 border-dashed border-slate-300 rounded-xl bg-white">
                                    <FaIdCard size={40} className="mb-2 opacity-50" />
                                    <p className="font-bold">No ID document uploaded yet.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                        <button 
                            onClick={() => handleDelete(selectedUser.id)}
                            className="text-red-500 font-bold text-sm hover:underline"
                        >
                            Delete User
                        </button>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setSelectedUser(null)}
                                className="px-6 py-3 rounded-xl font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    handleVerify(selectedUser.id, selectedUser.is_Identity_Verified);
                                    setSelectedUser(null);
                                }}
                                className={`px-6 py-3 rounded-xl font-bold text-white shadow-lg shadow-emerald-200/50 transition-all ${
                                    selectedUser.is_Identity_Verified 
                                    ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-200/50' 
                                    : 'bg-emerald-600 hover:bg-emerald-700'
                                }`}
                            >
                                {selectedUser.is_Identity_Verified ? 'Revoke Verification' : 'Approve & Verify'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;

    return (
        <div className="space-y-8 font-sans">
            <UserModal />
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">User Management</h1>
                    <p className="text-slate-500 mt-1 font-medium">Manage user accounts, roles, and KYC verifications.</p>
                </div>
                {/* Search */}
                <div className="relative w-full md:w-auto">
                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input 
                        type="text" 
                        placeholder="Search users..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:ring-2 ring-emerald-500 outline-none w-full md:w-80 font-bold text-slate-700 transition-all"
                    />
                </div>
            </div>

            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                                <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">User Profile</th>
                                <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Role</th>
                                <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Verification</th>
                                <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <img 
                                                src={loadImage(user.profile?.picture)} 
                                                alt="" 
                                                className="w-12 h-12 rounded-full object-cover bg-slate-200 border-2 border-white shadow-sm" 
                                            />
                                            <div>
                                                <p className="font-black text-slate-800 text-sm">{user.profile?.name || user.username}</p>
                                                <p className="text-xs font-bold text-slate-400">@{user.username}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wide">
                                            {user.Roles?.map(r => r.RoleName).join(', ') || 'User'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5">
                                        {user.is_Identity_Verified ? (
                                            <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-black bg-emerald-100 px-3 py-1.5 rounded-lg">
                                                <FaCheckCircle /> VERIFIED
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-orange-600 text-xs font-black bg-orange-100 px-3 py-1.5 rounded-lg">
                                                <FaUserShield /> PENDING
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => setSelectedUser(user)}
                                                className="p-2.5 text-slate-500 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 rounded-xl transition-colors"
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>
                                            <button 
                                                onClick={() => handleVerify(user.id, user.is_Identity_Verified)}
                                                className={`p-2.5 rounded-xl transition-colors ${
                                                    user.is_Identity_Verified 
                                                    ? 'text-orange-500 hover:text-orange-700 bg-orange-50 hover:bg-orange-100' 
                                                    : 'text-emerald-500 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                                                }`}
                                                title={user.is_Identity_Verified ? "Revoke Verification" : "Approve User"}
                                            >
                                                <FaUserShield />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(user.id)}
                                                className="p-2.5 text-slate-400 hover:text-red-600 bg-slate-50 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete User"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminUsersPage;
